import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Star } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Entrada, Rotulo } from '../../componentes/formulario';
import { useCamadaFlutuante } from '../../componentes/menu';
import { Modal } from '../../componentes/sobreposicao';
import { contagemConfere } from './crm-filtros';
import { useListas } from './crm-listas';

/**
 * CRM no novo desenho: peças pequenas das páginas do CRM. `Popover` é o painel não modal do núcleo (`useCamadaFlutuante`:
 * Esc, clique fora e Tab para fora fecham, e o painel não sai da janela), com o gatilho no formato dos filtros do clone.
 */
export const CLASSE_FILTRO = 'flex h-10 w-full min-w-0 max-w-full items-center gap-2 rounded-lg border border-borda-campo bg-campo px-3 text-sm text-tinta-1 hover:bg-sup-2';

export function Popover({ rotulo, gatilho, children, classePainel = 'left-0 w-72', classeGatilho = CLASSE_FILTRO, ...dados }: { rotulo: string; gatilho: ReactNode; children: (fechar: () => void) => ReactNode; classePainel?: string; classeGatilho?: string; [dado: `data-${string}`]: string }) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const id = useId();
  const fechar = useCallback(() => setAberto(false), []);
  const { aoTeclar } = useCamadaFlutuante({ aberto, fechar, raiz, painel, gatilho: () => raiz.current?.querySelector<HTMLElement>(':scope > button') });
  return (
    <div ref={raiz} className="relative min-w-0" onKeyDown={aoTeclar} {...dados}>
      <button type="button" aria-label={rotulo} title={rotulo} aria-expanded={aberto} aria-controls={aberto ? id : undefined} onClick={() => setAberto((x) => !x)} className={classeGatilho}>
        {gatilho}
        <ChevronDown className="ml-auto size-4 shrink-0 text-tinta-3" aria-hidden />
      </button>
      {aberto && (
        // tabIndex -1: o clique numa parte sem foco do painel (o rótulo da caixa) deixa o foco nele, e não na área do conteúdo
        // (que tem tabIndex e fecharia a camada pelo "foco saiu").
        <div ref={painel} id={id} role="group" tabIndex={-1} aria-label={rotulo} className={`absolute z-40 mt-1 flex max-w-screen flex-col gap-1 rounded-lg border border-borda bg-sup-2 p-2 text-tinta-1 shadow-pop outline-none ${classePainel}`}>
          {children(fechar)}
        </div>
      )}
    </div>
  );
}

/** Filtro de escolha múltipla (responsáveis, tipos): caixas de marcar dentro do popover, com "limpar". */
export function FiltroMultiplo({
  rotulo,
  resumo,
  icone,
  opcoes,
  marcados,
  aoMudar,
  limpar,
  extra,
  ...dados
}: {
  rotulo: string;
  resumo: string;
  icone?: ReactNode;
  opcoes: { valor: string; rotulo: string }[];
  marcados: string[];
  aoMudar: (v: string[]) => void;
  limpar: string;
  extra?: ReactNode;
  [dado: `data-${string}`]: string;
}) {
  const gatilho = (
    <>
      {icone}
      <span className="truncate" title={resumo}>
        {resumo}
      </span>
    </>
  );
  return (
    <Popover rotulo={rotulo} {...dados} gatilho={gatilho}>
      {() => (
        <>
          {extra}
          <ul className="flex max-h-72 flex-col overflow-y-auto">
            {opcoes.map((o) => (
              <li key={o.valor}>
                <label className="flex min-h-9 items-center gap-2 rounded px-2 text-sm hover:bg-sup-3">
                  <input type="checkbox" checked={marcados.includes(o.valor)} onChange={(e) => aoMudar(e.target.checked ? [...marcados, o.valor] : marcados.filter((x) => x !== o.valor))} />
                  <span className="min-w-0 truncate" title={o.rotulo}>{o.rotulo}</span>
                </label>
              </li>
            ))}
          </ul>
          {marcados.length > 0 && (
            <button type="button" className="self-start rounded px-2 py-1 text-sm font-medium text-acento-forte hover:underline" onClick={() => aoMudar([])}>
              {limpar}
            </button>
          )}
        </>
      )}
    </Popover>
  );
}

/** Iniciais do nome (avatar dos responsáveis). */
export const iniciais = (nome: string) =>
  nome
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Baixa um texto como arquivo (a exportação em CSV das ações em massa). */
export function baixarTexto(nome: string, texto: string, tipo = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([texto], { type: tipo }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Confirmação pela contagem (ações em massa do RD): a pessoa digita quantos itens serão alterados e marca que entende que não
 * dá para desfazer; só então o botão libera. `corpo` traz o campo extra da ação (o motivo da perda, o valor do campo).
 */
export function ConfirmarPorContagem({
  aberto,
  titulo,
  texto,
  pergunta,
  botao,
  n,
  corpo,
  podeConfirmar = true,
  carregando,
  aoFechar,
  aoConfirmar,
}: {
  aberto: boolean;
  titulo: string;
  texto: ReactNode;
  pergunta: string;
  botao: string;
  n: number;
  corpo?: ReactNode;
  podeConfirmar?: boolean;
  carregando?: boolean;
  aoFechar: () => void;
  aoConfirmar: () => void;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const id = useId();
  const [digitado, setDigitado] = useState('');
  const [ciente, setCiente] = useState(false);
  const liberado = podeConfirmar && contagemConfere(digitado, n, ciente);
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!liberado} carregando={carregando} onClick={aoConfirmar} data-confirmar-contagem="">
            {botao}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-sm" data-confirmacao-contagem="">
        <p>{texto}</p>
        {corpo}
        <p className="text-tinta-3">{t('crm.contagem.ajuda')}</p>
        <Rotulo id={`${id}-n`} rotulo={pergunta} obrigatorio>
          <Entrada id={`${id}-n`} inputMode="numeric" value={digitado} onChange={(e) => setDigitado(e.target.value)} placeholder={t('crm.contagem.placeholder')} data-contagem="" />
        </Rotulo>
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-0.5" checked={ciente} onChange={(e) => setCiente(e.target.checked)} data-ciente="" />
          {t('crm.contagem.ciente')}
        </label>
      </div>
    </Modal>
  );
}

/**
 * Qualificação em estrelas (0 a 5): só leitura, ou clicável para escolher. `compacta` (lote crm5, E7: o cartão do kanban, como
 * na origem) mostra uma estrela pequena com o número, com o mesmo nome acessível.
 */
export function Estrelas({ valor, aoMudar, rotulo, compacta }: { valor: number; aoMudar?: (v: number) => void; rotulo: string; compacta?: boolean }) {
  // Lote 5: o nome de cada nível vem das Configurações do CRM (Qualificação).
  const niveis = useListas().data?.niveis ?? [];
  const nomeDoNivel = (n: number) => niveis[n - 1];
  if (!aoMudar && compacta)
    return (
      <span className="inline-flex items-center gap-0.5 text-tinta-2" role="img" aria-label={nomeDoNivel(valor) ? `${rotulo}: ${nomeDoNivel(valor)}` : rotulo} title={nomeDoNivel(valor) ? `${rotulo}: ${nomeDoNivel(valor)}` : rotulo} data-estrela-compacta="">
        <Star className={`size-3.5 text-dourado ${valor > 0 ? 'fill-current' : 'opacity-40'}`} aria-hidden />
        <span className="numero">{valor}</span>
      </span>
    );
  if (!aoMudar)
    return (
      <span className="inline-flex items-center gap-0.5 text-dourado" role="img" aria-label={nomeDoNivel(valor) ? `${rotulo}: ${nomeDoNivel(valor)}` : rotulo} title={nomeDoNivel(valor) ? `${rotulo}: ${nomeDoNivel(valor)}` : rotulo}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`size-3.5 ${n <= valor ? 'fill-current' : 'text-tinta-3 opacity-40'}`} aria-hidden />
        ))}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-0.5" role="group" aria-label={rotulo}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" aria-pressed={n <= valor} aria-label={nomeDoNivel(n) ? `${n}: ${nomeDoNivel(n)}` : `${n}`} title={nomeDoNivel(n)} onClick={() => aoMudar(n === valor ? 0 : n)} className="rounded p-0.5 text-dourado hover:bg-sup-3">
          <Star className={`size-4 ${n <= valor ? 'fill-current' : 'text-tinta-3 opacity-50'}`} aria-hidden />
        </button>
      ))}
    </span>
  );
}
